import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Check, Rocket, X } from "lucide-react";
import {
  useSetupWizard,
  type WizardPasoId,
} from "@/contexts/SetupWizardContext";
import { Etiqueta } from "./ui";
import PasoHorarioCitas from "./PasoHorarioCitas";
import PasoServicios from "./PasoServicios";
import PasoProfesionales from "./PasoProfesionales";
import PasoSalas from "./PasoSalas";
import PasoHorarioMesas from "./PasoHorarioMesas";
import PasoMesas from "./PasoMesas";
import PasoEnlace from "./PasoEnlace";
import type { PasoProps } from "./types";

/**
 * ASISTENTE GUIADO — la ventana (27-sep-2026, petición de Luis).
 *
 * Es distinta a la guía que ya había (`/appointments/guide`) en lo único que
 * importaba: aquí NO se enlaza a las pantallas, SE RELLENAN LOS DATOS. El
 * cliente no tiene que entender el panel para dejar su negocio funcionando; va
 * contestando y al final se le da su enlace.
 *
 * DECISIONES DE FORMA:
 *  - UN SOLO botón principal ("Continuar"). Cada paso le dice a la ventana qué
 *    hacer antes de avanzar (guardar el horario, comprobar que hay un servicio)
 *    con `registrarAvance`; si no se puede, el paso avisa y no se mueve.
 *  - Se puede cerrar SIEMPRE ("Más tarde"). Un asistente que secuestra el panel
 *    de alguien que solo quería mirar las reservas de hoy es un estorbo.
 *  - Los pasos se pueden pulsar en cualquier orden: el orden que trae el backend
 *    es el recomendado, no una cárcel.
 *  - Alturas en `dvh` y no `vh`: en iOS, con `vh` la hoja crece más que la
 *    pantalla visible y la X se queda fuera (ya pasó con 40 modales).
 */

interface MetaPaso {
  titulo: string;
  ayuda: string;
}

/** Título y frase de cada paso, por modo de negocio. */
const META: Record<"appointments" | "tables", Partial<Record<WizardPasoId, MetaPaso>>> = {
  appointments: {
    horario: {
      titulo: "Tu horario",
      ayuda: "Los días y las horas en las que tu centro está abierto.",
    },
    servicios: {
      titulo: "Tus servicios",
      ayuda: "Qué puede pedir tu cliente y cuánto dura cada cosa.",
    },
    profesionales: {
      titulo: "Quién atiende",
      ayuda: "Las personas que dan las citas, con sus días y sus servicios.",
    },
    salas: {
      titulo: "Salas o cabinas",
      ayuda: "Dónde se atiende cada cita. Puedes saltártelo.",
    },
    enlace: {
      titulo: "Tu enlace de citas",
      ayuda: "La dirección que le das a tus clientes para pedir cita.",
    },
  },
  tables: {
    horario: {
      titulo: "Tu horario y tus turnos",
      ayuda: "Los días que abres y las horas a las que se puede reservar.",
    },
    mesas: {
      titulo: "Tu sala y tus mesas",
      ayuda: "Para que cada reserva se siente sola en su mesa.",
    },
    enlace: {
      titulo: "Tu enlace de reservas",
      ayuda: "La dirección que le das a tus clientes para reservar.",
    },
  },
};

/** Qué componente pinta cada paso, según el modo. */
function componenteDePaso(
  modo: "appointments" | "tables",
  paso: WizardPasoId,
): ((props: PasoProps) => JSX.Element) | null {
  if (modo === "appointments") {
    if (paso === "horario") return PasoHorarioCitas;
    if (paso === "servicios") return PasoServicios;
    if (paso === "profesionales") return PasoProfesionales;
    if (paso === "salas") return PasoSalas;
    if (paso === "enlace") return PasoEnlace;
    return null;
  }
  if (paso === "horario") return PasoHorarioMesas;
  if (paso === "mesas") return PasoMesas;
  if (paso === "enlace") return PasoEnlace;
  return null;
}

export default function SetupWizardModal() {
  const { estado, cerrar, refrescar, pasoInicial } = useSetupWizard();
  const modo = estado?.bookingMode === "appointments" ? "appointments" : "tables";
  const pasos = useMemo(() => estado?.pasos ?? [], [estado?.pasos]);

  /** Primer paso sin hacer (sin contar el del enlace); si no queda ninguno, el enlace. */
  const primeroPendiente = useMemo(() => {
    const pendiente = pasos.find((p) => p.id !== "enlace" && !p.hecho);
    return pendiente?.id ?? pasos[pasos.length - 1]?.id ?? "horario";
  }, [pasos]);

  const [pasoActual, setPasoActual] = useState<WizardPasoId>(
    pasoInicial ?? primeroPendiente,
  );

  const avanceRef = useRef<(() => Promise<boolean>) | null>(null);
  const [textoAvance, setTextoAvance] = useState<string | null>(null);
  const [avanzando, setAvanzando] = useState(false);

  const registrarAvance = useCallback(
    (fn: (() => Promise<boolean>) | null) => {
      avanceRef.current = fn;
    },
    [],
  );
  const registrarTextoAvance = useCallback((texto: string | null) => {
    setTextoAvance(texto);
  }, []);

  /**
   * Cambia de paso limpiando lo que registró el anterior. Se hace aquí y no en
   * un efecto a propósito: los efectos de los hijos corren ANTES que los del
   * padre, así que un reseteo en efecto borraría lo que el paso nuevo acaba de
   * registrar.
   */
  const irAPaso = useCallback((paso: WizardPasoId) => {
    avanceRef.current = null;
    setTextoAvance(null);
    setPasoActual(paso);
  }, []);

  const indice = pasos.findIndex((p) => p.id === pasoActual);
  const esUltimo = indice === pasos.length - 1;
  const pasoDef = pasos[indice] ?? null;

  const continuar = async () => {
    const accion = avanceRef.current;
    if (accion) {
      setAvanzando(true);
      let ok = false;
      try {
        ok = await accion();
      } finally {
        setAvanzando(false);
      }
      if (!ok) return;
    }
    if (esUltimo) {
      cerrar();
      return;
    }
    const siguiente = pasos[indice + 1];
    if (siguiente) irAPaso(siguiente.id);
  };

  // Escape cierra, igual que la X.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cerrar();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [cerrar]);

  if (!estado || !pasoDef) return null;

  const meta = META[modo][pasoActual] ?? { titulo: "Configuración", ayuda: "" };
  const Componente = componenteDePaso(modo, pasoActual);
  const hechos = pasos.filter((p) => p.id !== "enlace" && p.hecho).length;
  const totalConfigurables = pasos.filter((p) => p.id !== "enlace").length;

  return (
    <div
      // ARRIBA en móvil, centrada en escritorio. Una hoja pegada abajo es más
      // bonita, pero en iOS el teclado la tapa (el elemento fijo se ancla al
      // viewport de maquetación, no al visible) y este asistente es todo
      // formularios. Mismo criterio que la ventana de renombrar del inbox.
      className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wizard-titulo"
    >
      <div className="bg-white w-full sm:max-w-2xl rounded-b-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92dvh] mt-[var(--safe-top)] sm:mt-0">
        {/* Cabecera */}
        <div className="flex items-start gap-3 p-4 border-b border-gray-100">
          <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
            <Rocket className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="wizard-titulo" className="font-semibold text-gray-900">
              Pongamos tu negocio en marcha
            </h2>
            <p className="text-xs text-gray-500">
              {estado.bookingMode === "appointments"
                ? "Al terminar, tus clientes podrán pedir cita por internet."
                : "Al terminar, tendrás tu horario y tus mesas listos."}
              {estado.sede ? ` · ${estado.sede.nombre}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={() => cerrar()}
            aria-label="Cerrar"
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Pasos: se pueden pulsar para saltar de uno a otro */}
        <div className="px-4 pt-3">
          <div className="flex items-center gap-1.5 overflow-x-auto pb-2">
            {pasos.map((p, i) => {
              const activo = p.id === pasoActual;
              const titulo = META[modo][p.id]?.titulo ?? p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => irAPaso(p.id)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    activo
                      ? "bg-primary-600 text-white"
                      : p.hecho
                        ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {p.hecho ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <span
                      className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
                        activo ? "bg-white/20" : "bg-white text-gray-500"
                      }`}
                    >
                      {i + 1}
                    </span>
                  )}
                  {titulo}
                </button>
              );
            })}
          </div>
          <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-500"
              style={{
                width: `${
                  totalConfigurables === 0
                    ? 100
                    : (hechos / totalConfigurables) * 100
                }%`,
              }}
            />
          </div>
        </div>

        {/* Contenido del paso */}
        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-900">{meta.titulo}</h3>
            {pasoActual !== "enlace" && (
              <Etiqueta obligatorio={pasoDef.obligatorio} />
            )}
          </div>
          {meta.ayuda && (
            <p className="text-sm text-gray-500 mb-3">{meta.ayuda}</p>
          )}
          {Componente && (
            <Componente
              estado={estado}
              refrescar={refrescar}
              registrarAvance={registrarAvance}
              registrarTextoAvance={registrarTextoAvance}
              irAPaso={irAPaso}
            />
          )}
        </div>

        {/* Pie */}
        <div className="flex items-center gap-2 p-4 border-t border-gray-100 pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
          {indice > 0 ? (
            <button
              type="button"
              onClick={() => irAPaso(pasos[indice - 1].id)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-4 h-4" />
              Atrás
            </button>
          ) : (
            <button
              type="button"
              onClick={() => cerrar()}
              className="px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100 rounded-lg"
            >
              Más tarde
            </button>
          )}
          <div className="flex-1" />
          {esUltimo && (
            <button
              type="button"
              onClick={() => cerrar(true)}
              className="px-3 py-2 text-xs font-medium text-gray-400 hover:text-gray-600"
            >
              No volver a enseñarlo
            </button>
          )}
          <button
            type="button"
            onClick={continuar}
            disabled={avanzando}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
          >
            {avanzando
              ? "Guardando…"
              : (textoAvance ?? (esUltimo ? "Terminar" : "Continuar"))}
          </button>
        </div>
      </div>
    </div>
  );
}
